import React, { useState, Fragment } from 'react';
import classNames from 'classnames';
import { FiCheck, FiMinus } from 'react-icons/fi';
import { TbPlus, TbX } from 'react-icons/tb';

import { InfoTooltip } from '@components/InfoTooltip';
import { StatusBadge, statusBadgeTones } from '@components/StatusBadge';
import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';

import styles from './FeatureComparisonTable.module.scss';

const PLAN_ACCENT_COLOR = {
  starter: 'var(--success-600)',
  grow: 'var(--purple-600)',
  enterprise: 'var(--orange-600)',
};

const ToggleIcon = ({ isOpen }) =>
  isOpen ? (
    <TbX className={styles.chevron} />
  ) : (
    <TbPlus className={styles.chevron} />
  );

const FeatureTooltip = ({ label, tooltip }) =>
  tooltip ? (
    <InfoTooltip
      title={label}
      description={tooltip}
      showIcon={false}
      trigger="hover"
    />
  ) : null;

const AvailabilityCell = ({ available, planKey, popular, bordered }) => (
  <div
    className={classNames(styles.cell, {
      [styles.cell_unavailable]: !available,
      [styles.cell_popularColumn]: popular,
      [styles.cell_bordered]: bordered,
    })}
    style={available ? { color: PLAN_ACCENT_COLOR[planKey] } : undefined}
  >
    {available ? <FiCheck /> : <FiMinus />}
  </div>
);

// Every "row" component below is a Fragment of flat grid items — no wrapping
// div — so every cell is a direct child of the single shared grid in
// FeatureComparisonTableComponent. That's what keeps every row's columns
// pixel-aligned: they're all placed by the same grid instance, not by many
// independent per-row grids that can drift out of sync with each other.

const SubFeatureRow = ({ subFeature, plans }) => (
  <Fragment>
    <div className={classNames(styles.labelCell, styles.labelCell_subFeature)}>
      <span className={styles.connector}>↳</span>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Normal}
        color="gray-600"
      >
        {subFeature.label}
      </Text>
      <FeatureTooltip label={subFeature.label} tooltip={subFeature.tooltip} />
    </div>
    {plans.map((plan) => (
      <AvailabilityCell
        key={plan.key}
        planKey={plan.key}
        popular={plan.popular}
        available={Boolean(subFeature.availability?.[plan.key])}
      />
    ))}
  </Fragment>
);

const FeatureRow = ({ feature, plans }) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Fragment>
      <div
        className={classNames(
          styles.labelCell,
          styles.labelCell_feature,
          styles.labelCell_bordered
        )}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <ToggleIcon isOpen={isOpen} />
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
          color="gray-800"
        >
          {feature.label}
        </Text>
        <FeatureTooltip label={feature.label} tooltip={feature.tooltip} />
      </div>
      {plans.map((plan) => (
        <AvailabilityCell
          key={plan.key}
          planKey={plan.key}
          popular={plan.popular}
          bordered
          available={Boolean(feature.availability?.[plan.key])}
        />
      ))}
      {isOpen &&
        feature.subFeatures.map((subFeature) => (
          <SubFeatureRow
            key={subFeature.key}
            subFeature={subFeature}
            plans={plans}
          />
        ))}
    </Fragment>
  );
};

const CategorySection = ({ category, plans }) => (
  <Fragment>
    <div className={styles.categoryHeading}>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Bold}
        color="purple-700"
        className={styles.categoryLabel}
      >
        {category.label}
      </Text>
    </div>
    {category.features.map((feature) => (
      <FeatureRow key={feature.key} feature={feature} plans={plans} />
    ))}
  </Fragment>
);

export const FeatureComparisonTableComponent = ({ categories, plans }) => (
  <div className={styles.table} style={{ '--plan-count': plans.length }}>
    <Text
      variant={textVariantsEnum.Text_md}
      fontWeight={fontWeightEnum.Bold}
      color="gray-900"
      className={styles.compareLabel}
    >
      Compare Features
    </Text>
    {plans.map((plan) => (
      <div
        key={plan.key}
        className={classNames(styles.planHeaderCell, {
          [styles.cell_popularColumn]: plan.popular,
        })}
      >
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Bold}
          color="gray-900"
        >
          {plan.label}
        </Text>
        {plan.popular && (
          <StatusBadge
            label="Most Popular"
            tone={statusBadgeTones.purple}
            className={styles.popularBadge}
          />
        )}
      </div>
    ))}

    {categories.map((category) => (
      <CategorySection key={category.key} category={category} plans={plans} />
    ))}
  </div>
);

FeatureComparisonTableComponent.displayName = 'FeatureComparisonTable';
